import { useMemo } from 'react'
import { useSearchParams, type To } from 'react-router-dom'

/**
 * Which job is open, and which of its reports.
 *
 * A job is never a page. Opening one adds `?jobId=` to whatever screen the
 * person is already on — the Jobs list, the board, a client, the inbox — and
 * the popup is drawn over it by `JobOverlay`, mounted once in `AppShell`. So
 * closing a job puts them back exactly where they were, with the list's scroll,
 * filters and page intact, because that screen never unmounted. Decision 61.
 *
 * Two params rather than component state, for the reasons the board already
 * kept its selection in the URL: a job someone is looking at can be pasted into
 * a message, the back button closes the popup, and a refresh does not throw it
 * away.
 *
 * - `jobId` — the job card.
 * - `report` — a `JobService` id, drawn as a second popup over the first.
 * - `card=full` — in the field app only: the office's tabbed card over the
 *   technician's sheet, for the activity, the contacts and the forms.
 * - `cardTab` — which of the card's tabs it opens on, when a screen sends
 *   somebody to a job for one thing: Billing's "Invoice it" opens Billing.
 *   Read once when the card opens; changing tab does not write it back.
 *
 * Opening pushes a history entry so Back closes. Closing replaces, so Back
 * after closing goes on past rather than reopening what was just shut.
 */

export const JOB_PARAM = 'jobId'
export const REPORT_PARAM = 'report'
export const CARD_PARAM = 'card'
export const TAB_PARAM = 'cardTab'

/** The job card tabs a link may open on. */
export type JobCardTab = 'work' | 'billing' | 'schedule' | 'client' | 'files'

export type JobOverlay = {
  /** The open job, or null when nothing is open. */
  jobId: string | null
  /** The open report's `JobService` id, or null. */
  jobServiceId: string | null
  /** The field app has the full tabbed card open over its own sheet. */
  fullCard: boolean
  openJob: (jobId: string, tab?: JobCardTab) => void
  openReport: (jobServiceId: string) => void
  openFullCard: () => void
  closeFullCard: () => void
  /** Shuts the job, and the report with it. */
  closeJob: () => void
  /** Shuts the report and leaves the job open behind it. */
  closeReport: () => void
  /**
   * Where a link that opens a job should point: this page, plus the job. Used
   * rather than a handler wherever the thing being clicked is a row or a job
   * number, so it can still be opened in a new tab.
   */
  hrefFor: (jobId: string, tab?: JobCardTab) => To
}

export function useJobOverlay(): JobOverlay {
  const [searchParams, setSearchParams] = useSearchParams()

  // The string is the real dependency; the object identity changes on every
  // render even when nothing about the query has.
  const search = searchParams.toString()

  return useMemo(() => {
    const current = new URLSearchParams(search)

    const next = (mutate: (params: URLSearchParams) => void): URLSearchParams => {
      const params = new URLSearchParams(search)
      mutate(params)
      return params
    }

    return {
      jobId: current.get(JOB_PARAM),
      jobServiceId: current.get(REPORT_PARAM),
      fullCard: current.get(CARD_PARAM) === 'full',

      openJob: (jobId: string, tab?: JobCardTab) =>
        setSearchParams(
          next((params) => {
            params.set(JOB_PARAM, jobId)
            params.delete(REPORT_PARAM)
            params.delete(CARD_PARAM)
            if (tab) params.set(TAB_PARAM, tab)
            else params.delete(TAB_PARAM)
          }),
        ),

      openReport: (jobServiceId: string) =>
        setSearchParams(next((params) => params.set(REPORT_PARAM, jobServiceId))),

      openFullCard: () => setSearchParams(next((params) => params.set(CARD_PARAM, 'full'))),

      closeFullCard: () =>
        setSearchParams(next((params) => params.delete(CARD_PARAM)), { replace: true }),

      closeJob: () =>
        setSearchParams(
          next((params) => {
            params.delete(JOB_PARAM)
            params.delete(REPORT_PARAM)
            params.delete(CARD_PARAM)
            params.delete(TAB_PARAM)
          }),
          { replace: true },
        ),

      closeReport: () =>
        setSearchParams(next((params) => params.delete(REPORT_PARAM)), { replace: true }),

      hrefFor: (jobId: string, tab?: JobCardTab) => ({
        search: `?${next((params) => {
          params.set(JOB_PARAM, jobId)
          params.delete(REPORT_PARAM)
          params.delete(CARD_PARAM)
          if (tab) params.set(TAB_PARAM, tab)
          else params.delete(TAB_PARAM)
        })}`,
      }),
    }
  }, [search, setSearchParams])
}

/**
 * The same href without the rest of the hook, for the many rows that only link.
 */
export function useJobHref(): (jobId: string, tab?: JobCardTab) => To {
  return useJobOverlay().hrefFor
}
